import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Tooltip from '@mui/material/Tooltip';
import ShareIcon from '@mui/icons-material/Share';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SendRounded from '@mui/icons-material/SendRounded';
import CircularProgress from '@mui/material/CircularProgress';
import EditOutlined from '@mui/icons-material/EditOutlined';
import type { NoteTag, NoteComment, PendingSelection } from '../types';
import type { CaseTag } from '../notesApi';
import { useToasts } from 'react-toast-notifications';
import { formatNoteTimestamp } from '../utils/formatters';
import TagInput from './TagInput';

type HighlightPopupProps = {
  pendingSelection: PendingSelection | null;
  activeNoteId: string | null;
  isEditingExisting: boolean;
  isHighlightOnly: boolean;
  isTagMode: boolean;
  isRemoteNote?: boolean;
  isLocalOnly?: boolean;
  onHighlight: () => void;
  onNote: () => void;
  onTag: () => void;
  onDismiss: () => void;
  onNoteChange: (id: string, text: string) => void;
  onTagsChange: (id: string, tags: NoteTag[]) => void;
  onCreateTag: (name: string) => Promise<NoteTag | null>;
  onRemove: (id: string) => void;
  noteText: string;
  selectedTags: NoteTag[];
  availableTags: CaseTag[];
  readOnly?: boolean;
  visibility: 'private' | 'team';
  onVisibilityToggle: () => void;
  createdByName?: string;
  createdAt?: number;
  comments?: NoteComment[];
  onSubmitComment?: (commentText: string) => Promise<boolean>;
};

const HighlightPopup: React.FC<HighlightPopupProps> = ({
  pendingSelection,
  activeNoteId,
  isEditingExisting,
  isHighlightOnly,
  isTagMode,
  isRemoteNote = false,
  isLocalOnly = false,
  onHighlight,
  onNote,
  onTag,
  onDismiss,
  onNoteChange,
  onTagsChange,
  onCreateTag,
  onRemove,
  noteText,
  selectedTags,
  availableTags,
  readOnly = false,
  visibility,
  onVisibilityToggle,
  createdByName,
  createdAt,
  comments,
  onSubmitComment,
}) => {
  const { addToast } = useToasts();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const tagInputRef = useRef<HTMLInputElement | null>(null);
  const [noteAnchorEl, setNoteAnchorEl] = useState<HTMLElement | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isEditingComment, setIsEditingComment] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const initialNoteTextRef = useRef(noteText);
  const noteTextChanged = noteText !== initialNoteTextRef.current;

  const virtualAnchor = useMemo(() => {
    if (!pendingSelection) return null;
    const rect = pendingSelection.anchorRect;
    return {
      getBoundingClientRect: () => rect,
      clientWidth: rect.width,
      clientHeight: rect.height,
    };
  }, [pendingSelection]);

  // Reset state when popup changes
  useEffect(() => {
    setShowDeleteConfirm(false);
    setIsEditingComment(false);
    setReplyText('');
    initialNoteTextRef.current = noteText;
  }, [activeNoteId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Query the <mark> element after DOM commit so TranscriptLine has rendered it.
  // Also re-query when visibility changes, because toggling visibility causes
  // TranscriptLine to unmount/remount the <mark> (different JSX wrapper for team vs private).
  useLayoutEffect(() => {
    if (!activeNoteId) {
      setNoteAnchorEl(null);
      return;
    }
    const el = document.querySelector(
      `[data-highlight-id="${activeNoteId}"]`,
    ) as HTMLElement | null;
    setNoteAnchorEl(el);
  }, [activeNoteId, visibility]);

  // Focus the tag input or textarea once the note panel has actually mounted
  useEffect(() => {
    if (!noteAnchorEl || readOnly) return;
    if (isTagMode && tagInputRef.current) {
      tagInputRef.current.focus();
    } else if (!isHighlightOnly && textareaRef.current) {
      const ta = textareaRef.current;
      ta.focus();
      const len = ta.value.length;
      ta.setSelectionRange(len, len);
    }
  }, [noteAnchorEl, readOnly, isHighlightOnly, isTagMode]);

  const handlePanelKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Skip if a child (e.g. TagInput dropdown) already handled this key
      if (e.defaultPrevented) return;

      if (e.key === 'Escape') {
        onDismiss();
      }
      // For highlight-only panel or note panel: Enter submits
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (isHighlightOnly || noteText.trim().length > 0) {
          onDismiss();
        } else if (noteTextChanged) {
          addToast('Note cannot be empty.', { appearance: 'warning', autoDismiss: true });
        }
      }
    },
    [onDismiss, isHighlightOnly, noteText, noteTextChanged, addToast],
  );

  const handleTagsChange = useCallback(
    (tags: NoteTag[]) => {
      if (activeNoteId) {
        onTagsChange(activeNoteId, tags);
      }
    },
    [activeNoteId, onTagsChange],
  );

  const hasTags = selectedTags && selectedTags.length > 0;
  const hasContent = !!(noteText || hasTags);
  const hasComments = comments && comments.length > 0;
  const effectiveReadOnly = readOnly || isRemoteNote;

  const handleClickAway = useCallback(() => {
    if (!isHighlightOnly && noteText.trim().length === 0 && noteTextChanged && activeNoteId) {
      addToast('Note cannot be empty.', { appearance: 'warning', autoDismiss: true });
      onNoteChange(activeNoteId, initialNoteTextRef.current);
    }
    onDismiss();
  }, [isHighlightOnly, noteText, noteTextChanged, activeNoteId, onNoteChange, onDismiss, addToast]);

  const handleSubmitReply = useCallback(async () => {
    const text = replyText.trim();
    if (!text || !onSubmitComment || isSubmittingReply) return;
    setIsSubmittingReply(true);
    try {
      const success = await onSubmitComment(text);
      if (success) setReplyText('');
    } finally {
      setIsSubmittingReply(false);
    }
  }, [replyText, onSubmitComment, isSubmittingReply]);

  const showToolbar = !!pendingSelection && !activeNoteId;
  const showPanel = !!activeNoteId && !!noteAnchorEl;

  return (
    <>
      {showToolbar && virtualAnchor && (
        <ClickAwayListener onClickAway={onDismiss}>
          <Popper
            open
            anchorEl={virtualAnchor as any}
            placement="top"
            disablePortal
            className="real-time__highlight-popper"
            modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
          >
            <div className="real-time__highlight-toolbar">
              <button type="button" className="real-time__highlight-btn" onClick={onHighlight}>
                Highlight
              </button>
              <button type="button" className="real-time__highlight-btn" onClick={onNote}>
                Note
              </button>
              <button type="button" className="real-time__highlight-btn" onClick={onTag}>
                Tag
              </button>
            </div>
          </Popper>
        </ClickAwayListener>
      )}

      {showPanel && (
        <ClickAwayListener onClickAway={handleClickAway}>
          <Popper
            open
            anchorEl={noteAnchorEl}
            placement="bottom-start"
            disablePortal
            className="real-time__highlight-popper"
            modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
          >
            <div
              className={`real-time__note-panel${isTagMode ? ' real-time__note-panel--wide' : ''}`}
              onKeyDown={handlePanelKeyDown}
            >
{/* Remote note share icon removed — non-creator users should not see top-row actions */}
              {isEditingExisting && !readOnly && !isRemoteNote && !hasComments && (
                <div className="real-time__note-top-row">
                  {noteText && !isEditingComment && (
                    <Tooltip title="Edit comment" arrow placement="top">
                      <button
                        type="button"
                        className="real-time__note-edit"
                        onClick={() => setIsEditingComment(true)}
                      >
                        <EditOutlined style={{ fontSize: 16 }} />
                      </button>
                    </Tooltip>
                  )}
                  {isLocalOnly ? (
                    <Tooltip title="Saved locally only" arrow placement="top">
                      <span className="real-time__visibility-toggle real-time__visibility-toggle--readonly">
                        <LockOutlinedIcon style={{ fontSize: 16 }} />
                      </span>
                    </Tooltip>
                  ) : (
                    <Tooltip
                      title={(() => {
                        const type =
                          isTagMode && hasTags ? 'tag' : hasContent ? 'note' : 'highlight';
                        return visibility === 'team'
                          ? `This ${type} is shared with team. You can click to make it private`
                          : `This ${type} is private. You can click to share with the team`;
                      })()}
                      arrow
                      placement="top"
                    >
                      <button
                        type="button"
                        className="real-time__visibility-toggle"
                        onClick={onVisibilityToggle}
                      >
                        {visibility === 'team' ? (
                          <ShareIcon style={{ fontSize: 16 }} />
                        ) : (
                          <LockOutlinedIcon style={{ fontSize: 16 }} />
                        )}
                      </button>
                    </Tooltip>
                  )}
                  <Tooltip
                    title={
                      isTagMode && hasTags
                        ? 'Delete tag'
                        : hasContent
                          ? 'Delete note'
                          : 'Delete highlight'
                    }
                    arrow
                    placement="top"
                  >
                    <button
                      type="button"
                      className="real-time__note-remove"
                      onClick={() => setShowDeleteConfirm(true)}
                    >
                      <DeleteOutlineIcon style={{ fontSize: 16 }} />
                    </button>
                  </Tooltip>
                </div>
              )}
              {showDeleteConfirm && (
                <div className="real-time__delete-confirm">
                  <span className="real-time__delete-confirm-text">
                    Delete this{' '}
                    {isTagMode && hasTags ? 'tag' : hasContent ? 'note' : 'highlight'}?
                  </span>
                  <div className="real-time__delete-confirm-actions">
                    <button
                      type="button"
                      className="real-time__delete-confirm-cancel"
                      onClick={() => setShowDeleteConfirm(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="real-time__delete-confirm-yes"
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        onRemove(activeNoteId!);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
              {isTagMode && (
                <TagInput
                  ref={tagInputRef}
                  selectedTags={selectedTags}
                  availableTags={availableTags}
                  onChange={handleTagsChange}
                  onCreateTag={onCreateTag}
                  readOnly={effectiveReadOnly || !!hasComments}
                />
              )}
              {!isHighlightOnly && isEditingExisting && !isEditingComment && (noteText || hasComments) && (
                <div className="real-time__note-comments-scroll">
                  {noteText && (
                    <div className="real-time__note-comment">
                      {createdByName && (
                        <span className="real-time__note-comment-author">{createdByName}</span>
                      )}
                      <p className="real-time__note-comment-text">{noteText}</p>
                      {createdAt && (
                        <span className="real-time__note-comment-time">
                          {formatNoteTimestamp(createdAt)}
                        </span>
                      )}
                    </div>
                  )}
                  {hasComments &&
                    comments.map((c) => (
                      <div key={c.commentId} className="real-time__note-comment">
                        {c.createdBy?.name && (
                          <span className="real-time__note-comment-author">{c.createdBy.name}</span>
                        )}
                        <p className="real-time__note-comment-text">{c.commentText}</p>
                        {c.createdAtUtc && (
                          <span className="real-time__note-comment-time">
                            {formatNoteTimestamp(c.createdAtUtc)}
                          </span>
                        )}
                      </div>
                    ))}
                </div>
              )}
              {!isHighlightOnly && (!isEditingExisting || isEditingComment) && (
                <div className="real-time__note-input-wrapper">
                  <textarea
                    ref={textareaRef}
                    className="real-time__note-input"
                    placeholder={isTagMode ? 'Add a comment...' : 'Add a note...'}
                    value={noteText}
                    onChange={
                      effectiveReadOnly
                        ? undefined
                        : (e) => onNoteChange(activeNoteId!, e.target.value)
                    }
                    readOnly={effectiveReadOnly}
                    rows={3}
                  />
                  {!effectiveReadOnly && noteTextChanged && noteText.trim().length > 0 && (
                    <button type="button" className="real-time__note-send" onClick={onDismiss}>
                      <SendRounded style={{ fontSize: 16 }} />
                    </button>
                  )}
                </div>
              )}
              {isEditingExisting && !isEditingComment && (
                <div className="real-time__note-input-wrapper">
                  <textarea
                    className="real-time__note-input real-time__note-reply-input"
                    placeholder="Reply..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    maxLength={2000}
                    disabled={isSubmittingReply}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSubmitReply();
                      }
                    }}
                    rows={2}
                  />
                  {replyText.trim().length > 0 && (
                    isSubmittingReply ? (
                      <span className="real-time__note-send">
                        <CircularProgress size={16} thickness={5} />
                      </span>
                    ) : (
                      <button type="button" className="real-time__note-send" onClick={handleSubmitReply}>
                        <SendRounded style={{ fontSize: 16 }} />
                      </button>
                    )
                  )}
                </div>
              )}
            </div>
          </Popper>
        </ClickAwayListener>
      )}
    </>
  );
};

export default HighlightPopup;
